import React, { useState } from 'react';
import { Box, Card, CardContent, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';

interface ChartSectionProps {
  title: string;
  /** The MUI X Chart element. Omitted (with `empty`) renders only the empty message. */
  chart?: React.ReactNode;
  /** The accessible data table carrying the same values as the chart (FR-005). */
  table: React.ReactNode;
  /** Label for the table-toggle icon button, e.g. "Show data table for …". */
  toggleLabel: string;
  empty?: boolean;
  emptyMessage?: string;
}

/**
 * Pairs a chart with a toggleable, visually-available data table carrying the
 * same values (spec 038 FR-005 — every chart needs a keyboard/screen-reader
 * accessible equivalent). The table is collapsed by default; toggling it
 * never re-fetches or resets chart state, so a plain local boolean is safe
 * (no effect-driven state here).
 */
const ChartSection: React.FC<ChartSectionProps> = ({
  title,
  chart,
  table,
  toggleLabel,
  empty,
  emptyMessage,
}) => {
  const [tableOpen, setTableOpen] = useState(false);

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" component="h2" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          <Tooltip title={toggleLabel}>
            <IconButton
              size="small"
              aria-label={toggleLabel}
              aria-expanded={tableOpen}
              onClick={() => setTableOpen((v) => !v)}
            >
              <TableChartOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        {empty ? (
          <Typography color="text.secondary" variant="body2">
            {emptyMessage}
          </Typography>
        ) : (
          <Box role="img" aria-label={title}>
            {chart}
          </Box>
        )}

        <Collapse in={tableOpen} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 2 }}>{table}</Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default ChartSection;
